import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/ui/Card';
import { rewardsService, RewardDefinition, UserReward } from '../services/rewardsService';
import {
  Trophy,
  Star,
  Users,
  Activity,
  CheckCircle2,
  Circle,
  ChevronRight,
  Coins,
} from 'lucide-react';

const CATEGORY_CONFIG = {
  ACTIVITY: {
    label: 'Activitate Fitness',
    icon: Activity,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
  },
  COMMUNITY: {
    label: 'Comunitate',
    icon: Users,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    badge: 'bg-purple-100 text-purple-700',
  },
  REFERRAL: {
    label: 'Invitatii',
    icon: Star,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
  },
};

type TabType = 'all' | 'ACTIVITY' | 'COMMUNITY' | 'REFERRAL';

export default function RewardsPage() {
  const navigate = useNavigate();
  const [definitions, setDefinitions] = useState<RewardDefinition[]>([]);
  const [earned, setEarned] = useState<UserReward[]>([]);
  const [totalCredits, setTotalCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [defs, myRewards] = await Promise.all([
        rewardsService.getDefinitions(),
        rewardsService.getMyRewards(),
      ]);
      setDefinitions(defs);
      setEarned(myRewards.rewards);
      setTotalCredits(myRewards.totalCreditsEarned);
    } catch (err) {
      console.error('Error loading rewards:', err);
    } finally {
      setLoading(false);
    }
  };

  const earnedKeys = new Set(earned.map(r => r.rewardDefinition.key));

  const filteredDefinitions = activeTab === 'all'
    ? definitions
    : definitions.filter(d => d.category === activeTab);

  const completedCount = definitions.filter(d => earnedKeys.has(d.key)).length;
  const progressPercent = definitions.length > 0
    ? Math.round((completedCount / definitions.length) * 100)
    : 0;

  const tabs: { key: TabType; label: string }[] = [
    { key: 'all', label: 'Toate' },
    { key: 'ACTIVITY', label: 'Activitate' },
    { key: 'COMMUNITY', label: 'Comunitate' },
    { key: 'REFERRAL', label: 'Invitatii' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <PageHeader title="Recompense" />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <PageHeader title="Recompense" />

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="text-center py-4">
            <div className="text-2xl font-bold text-blue-600">{completedCount}</div>
            <div className="text-xs text-gray-500 mt-0.5">Obtinute</div>
          </Card>
          <Card className="text-center py-4">
            <div className="text-2xl font-bold text-gray-800">{definitions.length}</div>
            <div className="text-xs text-gray-500 mt-0.5">Disponibile</div>
          </Card>
          <Card className="text-center py-4">
            <div className="text-2xl font-bold text-amber-600">{totalCredits}</div>
            <div className="text-xs text-gray-500 mt-0.5">Credite castigate</div>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Progres general</span>
            <span className="text-sm font-bold text-blue-600">{progressPercent}%</span>
          </div>
          <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-700"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {completedCount} din {definitions.length} recompense obtinute
          </p>
        </Card>

        {/* Referral CTA */}
        <button
          onClick={() => navigate('/referrals')}
          className="w-full flex items-center justify-between bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl px-5 py-4 shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className="font-bold text-sm">Invita prieteni, castiga credite</div>
              <div className="text-xs text-white/80">Pana la 150 credite per cont</div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-white/80" />
        </button>

        {/* Category Tabs */}
        <div className="flex gap-1.5 bg-white rounded-xl p-1.5 border border-gray-100 shadow-sm overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 min-w-fit py-2 px-3 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Reward Cards */}
        <div className="space-y-3">
          {filteredDefinitions.map(def => {
            const isEarned = earnedKeys.has(def.key);
            const earnedRecord = earned.find(r => r.rewardDefinition.key === def.key);
            const cfg = CATEGORY_CONFIG[def.category];
            const CategoryIcon = cfg.icon;

            return (
              <div
                key={def.id}
                className={`bg-white rounded-xl border transition-all ${
                  isEarned
                    ? 'border-green-200 shadow-sm'
                    : 'border-gray-100 shadow-sm'
                }`}
              >
                <div className="flex items-start gap-4 p-4">
                  {/* Status Icon */}
                  <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${
                    isEarned ? 'bg-green-50' : cfg.bg
                  }`}>
                    {isEarned ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : (
                      <CategoryIcon className={`w-6 h-6 ${cfg.color}`} />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className={`text-sm font-bold leading-tight ${
                        isEarned ? 'text-gray-700' : 'text-gray-900'
                      }`}>
                        {def.title}
                      </h3>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Coins className="w-3.5 h-3.5 text-amber-500" />
                        <span className="text-xs font-bold text-amber-600">+{def.creditValue}</span>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      {def.description}
                    </p>

                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
                        <CategoryIcon className="w-3 h-3" />
                        {cfg.label}
                      </span>

                      {isEarned && earnedRecord && (
                        <span className="text-xs text-green-600 font-medium">
                          Obtinut {new Date(earnedRecord.earnedAt).toLocaleDateString('ro-RO')}
                        </span>
                      )}

                      {!isEarned && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Circle className="w-3 h-3" />
                          Neobtinut
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredDefinitions.length === 0 && (
          <Card className="text-center py-12">
            <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Nicio recompensa in aceasta categorie</p>
          </Card>
        )}
      </div>
    </div>
  );
}
